"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { withLocalePrefix } from "../../i18n/locale-url";
import {
  LOCALE_PREFIX_WITH_PATH_RE,
  type LocaleString,
} from "../../i18n/settings";
import { isI18nTargetPage } from "../../i18n/target-pages";

import { Icons } from "./icons";
import { Link } from "./ui/link";

function getBasePath(pathname: string): string {
  const match = pathname.match(LOCALE_PREFIX_WITH_PATH_RE);
  if (match) return match[2] || "/";
  return pathname;
}

function buildPath(
  basePath: string,
  locale: LocaleString,
  searchParams: URLSearchParams,
): string {
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : "";
  return `${withLocalePrefix(basePath, locale)}${suffix}`;
}

const LOCALES: { code: LocaleString; label: string }[] = [
  { code: "ja", label: "日本語" },
  { code: "en", label: "English" },
  { code: "zh-CN", label: "簡体中文" },
  { code: "zh-TW", label: "繁體中文" },
];

function LanguageSelectorInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(() => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen((v) => !v);
  }, [open]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (!(e.target instanceof Node)) return;
      if (
        buttonRef.current?.contains(e.target) ||
        dropdownRef.current?.contains(e.target)
      )
        return;
      setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!isI18nTargetPage(pathname)) return null;

  const basePath = getBasePath(pathname);

  return (
    <div className="relative mx-[12px]">
      <button
        data-analytics-id="language_selector_opened_menu"
        ref={buttonRef}
        onClick={handleToggle}
        className="text-skogecha-80 flex items-center gap-[6px] rounded-full border border-[#d6d0c4] bg-white px-[12px] py-[6px] text-[12px] hover:bg-[#f5f3ef]"
      >
        <Icons.GlobeIcon />
        <span>Language</span>
        <Icons.ChevronDownLineIcon />
      </button>
      {open &&
        dropdownPos &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{ top: dropdownPos.top, right: dropdownPos.right }}
            className="fixed z-[200] min-w-[120px] overflow-hidden rounded-[8px] bg-white shadow-md"
          >
            {LOCALES.map((locale) => (
              <Link
                data-analytics-id="language_selector_selected_locale"
                key={locale.code}
                href={buildPath(basePath, locale.code, searchParams)}
                className="text-skogecha-100 hover:bg-slightbeige-100 block px-[16px] py-[12px] text-[13px]"
                onClick={() => setOpen(false)}
              >
                {locale.label}
              </Link>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

export function LanguageSelector() {
  return (
    <Suspense fallback={null}>
      <LanguageSelectorInner />
    </Suspense>
  );
}
